import React from "react";
import { createPortal } from "react-dom";
import {
  MdClose,
  MdFileDownload,
  MdAttachFile,
  MdSync,
  MdUpload,
  MdCheck,
  MdErrorOutline,
  MdCheckCircle,
  MdWarning,
  MdError,
} from "react-icons/md";
import toast from "react-hot-toast";
import { useBulkProductUploadModal } from "../../customHook/BulkProductUploadModal/useBulkProductUploadModal";

/**
 * Clean & Professional Industry-Ready Bulk Product Upload Modal built with Tailwind CSS.
 */
export default function BulkProductUploadModal({
  isOpen,
  onClose,
  onSubmit,
  onRefreshProducts,
  isLoading = false,
}) {
  const {
    selectedFile,
    isDragOver,
    parsedData,
    validationErrors,
    uploadResult,
    isUploading: hookUploading,
    fileInputRef,
    handleFileSelect,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    handleRemoveFile,
    handleFormSubmit,
    handleModalClose,
    clearUploadResult,
  } = useBulkProductUploadModal({
    isOpen,
    onClose,
    onSubmit,
    onRefreshProducts,
  });

  const uploading = isLoading || hookUploading;

  React.useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && !uploading) {
        handleModalClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, uploading, handleModalClose]);

  const handleDownloadSampleData = () => {
    const fileUrl = `${process.env.PUBLIC_URL || ""}/product_data.csv`;
    const link = document.createElement("a");
    link.href = fileUrl;
    link.setAttribute("download", "product_data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Downloaded product_data.csv!");
  };

  if (!isOpen || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-[1000] p-5"
      onClick={handleModalClose}
    >
      <div
        className="bg-white border border-slate-200 rounded-xl max-w-[850px] w-full min-h-[580px] max-h-[94vh] flex flex-col shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 px-6 sm:px-8 border-b border-slate-100 flex items-center justify-between shrink-0">
          <h2 className="m-0 text-slate-900 text-xl font-bold tracking-tight">
            {uploadResult ? "Bulk Upload Results" : "Upload Products"}
          </h2>
          <button
            className="bg-transparent border-0 text-slate-500 hover:text-slate-900 text-xl cursor-pointer p-1 rounded-md transition-colors flex items-center justify-center"
            onClick={handleModalClose}
            disabled={uploading}
            type="button"
            aria-label="Close"
          >
            <MdClose />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 sm:p-8 flex flex-col gap-4 flex-1 min-h-0 overflow-y-auto">
          {/* Always mounted hidden file input so file picker can be opened directly from results or upload view */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            style={{ display: "none" }}
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                handleFileSelect(e.target.files[0]);
              }
              e.target.value = "";
            }}
          />

          {/* API Upload Result View */}
          {uploadResult ? (
            <div className="flex flex-col gap-4">
              {/* Summary Status Banner */}
              <div
                className={`p-3.5 rounded-lg border flex items-center gap-3 animate-in fade-in duration-150 ${
                  uploadResult.status === "error" ||
                  (uploadResult.data?.failedCount > 0 &&
                    (!uploadResult.data?.succeededCount ||
                      uploadResult.data?.succeededCount === 0))
                    ? "bg-red-50/90 border-red-200 text-red-950"
                    : uploadResult.data?.failedCount > 0
                    ? "bg-amber-50/90 border-amber-200 text-amber-950"
                    : "bg-emerald-50/90 border-emerald-200 text-emerald-950"
                }`}
              >
                {uploadResult.status === "error" ||
                (uploadResult.data?.failedCount > 0 &&
                  (!uploadResult.data?.succeededCount ||
                    uploadResult.data?.succeededCount === 0)) ? (
                  <MdError size={22} className="text-red-600 shrink-0" />
                ) : uploadResult.data?.failedCount > 0 ? (
                  <MdWarning size={22} className="text-amber-600 shrink-0" />
                ) : (
                  <MdCheckCircle
                    size={22}
                    className="text-emerald-600 shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="m-0 text-sm font-bold">
                    {uploadResult.status === "error" ||
                    (uploadResult.data?.failedCount > 0 &&
                      (!uploadResult.data?.succeededCount ||
                        uploadResult.data?.succeededCount === 0))
                      ? "Bulk Upload Failed"
                      : uploadResult.data?.failedCount > 0
                      ? "Bulk Upload Completed with Errors"
                      : "Bulk Upload Completed Successfully"}
                  </h3>
                </div>
              </div>

              {/* Stat Cards */}
              {uploadResult.data && (
                <div className="grid grid-cols-3 gap-3 animate-in fade-in duration-150">
                  <div className="bg-white border border-slate-200 rounded-lg p-3 text-center shadow-xs">
                    <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      Total Products
                    </span>
                    <span className="block text-xl font-extrabold text-slate-900 mt-1">
                      {uploadResult.data?.total ?? parsedData.length}
                    </span>
                  </div>
                  <div className="bg-emerald-50/60 border border-emerald-200 rounded-lg p-3 text-center shadow-xs">
                    <span className="block text-[10px] font-bold text-emerald-700 uppercase tracking-wider">
                      Succeeded
                    </span>
                    <span className="block text-xl font-extrabold text-emerald-700 mt-1">
                      {uploadResult.data?.succeededCount ??
                        uploadResult.data?.successfulProducts?.length ??
                        0}
                    </span>
                  </div>
                  <div className="bg-red-50/60 border border-red-200 rounded-lg p-3 text-center shadow-xs">
                    <span className="block text-[10px] font-bold text-red-700 uppercase tracking-wider">
                      Failed
                    </span>
                    <span className="block text-xl font-extrabold text-red-700 mt-1">
                      {uploadResult.data?.failedCount ??
                        uploadResult.data?.failedProducts?.length ??
                        0}
                    </span>
                  </div>
                </div>
              )}

              {/* Failed Products Table */}
              {uploadResult?.data?.failedProducts?.length > 0 && (
                <div className="flex flex-col gap-2 animate-in fade-in duration-150">
                  <span className="text-xs font-bold text-red-700 uppercase tracking-wider flex items-center gap-1.5">
                    <MdErrorOutline className="text-red-600" size={16} />
                    <span>
                      Failed Products ({uploadResult.data.failedProducts.length})
                    </span>
                  </span>
                  <div className="border border-red-200 rounded-lg overflow-hidden max-h-64 overflow-y-auto shadow-xs">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead className="bg-red-100 sticky top-0 z-10 border-b border-red-200 text-red-900 font-bold uppercase text-[10px] tracking-wider">
                        <tr>
                          <th className="py-2.5 px-3 w-14 bg-red-100 text-center">
                            ROW
                          </th>
                          <th className="py-2.5 px-3 w-36 bg-red-100">SKU</th>
                          <th className="py-2.5 px-3 w-48 bg-red-100">TITLE</th>
                          <th className="py-2.5 px-3 bg-red-100">
                            ERROR DETAILS
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-red-100 bg-white">
                        {uploadResult.data.failedProducts.map((item, idx) => {
                          const rowNum =
                            item.row ?? item.rowIndex ?? item.line ?? idx + 1;
                          const sku = item.sku ?? item.productSku ?? "—";
                          const title =
                            item.title ??
                            item.productTitle ??
                            item.name ??
                            "—";
                          const errorMsg =
                            item.error ||
                            item.details ||
                            item.message ||
                            item.reason ||
                            "Failed to process product";

                          return (
                            <tr
                              key={idx}
                              className="hover:bg-red-50/50 transition-colors"
                            >
                              <td className="py-2.5 px-3 font-semibold text-slate-700 text-center align-top">
                                {rowNum}
                              </td>
                              <td className="py-2.5 px-3 font-mono font-semibold text-slate-900 align-top text-[11px] break-all">
                                {sku}
                              </td>
                              <td className="py-2.5 px-3 font-medium text-slate-800 align-top">
                                {title}
                              </td>
                              <td className="py-2.5 px-3 text-red-600 font-medium align-top leading-relaxed">
                                {Array.isArray(errorMsg)
                                  ? errorMsg.join(", ")
                                  : errorMsg}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Successful Products Table (Shown on partial success) */}
              {uploadResult?.data?.successfulProducts?.length > 0 &&
                uploadResult?.data?.failedProducts?.length > 0 && (
                  <div className="flex flex-col gap-2 animate-in fade-in duration-150">
                    <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider flex items-center gap-1.5">
                      <MdCheckCircle className="text-emerald-600" size={16} />
                      <span>
                        Successfully Created Products (
                        {uploadResult.data.successfulProducts.length})
                      </span>
                    </span>
                    <div className="border border-emerald-200 rounded-lg overflow-hidden max-h-40 overflow-y-auto shadow-xs">
                      <table className="w-full text-left text-xs border-collapse">
                        <thead className="bg-emerald-50 sticky top-0 z-10 border-b border-emerald-200 text-emerald-900 font-bold uppercase text-[10px] tracking-wider">
                          <tr>
                            <th className="py-2 px-3 w-36 bg-emerald-50">SKU</th>
                            <th className="py-2 px-3 bg-emerald-50">TITLE</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-emerald-100 bg-white">
                          {uploadResult.data.successfulProducts.map(
                            (prod, idx) => (
                              <tr
                                key={prod.sku || idx}
                                className="hover:bg-emerald-50/40"
                              >
                                <td className="py-2 px-3 font-mono font-semibold text-slate-900 text-[11px]">
                                  {prod.sku || "—"}
                                </td>
                                <td className="py-2 px-3 text-slate-800 font-medium">
                                  {prod.title || prod.name || "—"}
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
            </div>
          ) : (
            <>
              {/* Subtitle & Sample Download Button */}
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <p className="m-0 text-slate-500 text-xs sm:text-sm font-normal">
                  Add products to the CMS via CSV file
                </p>
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:border-slate-400 text-slate-700 hover:text-slate-900 text-xs font-semibold px-3 py-1.5 rounded-md transition-all shadow-xs cursor-pointer whitespace-nowrap"
                  onClick={handleDownloadSampleData}
                >
                  <MdFileDownload size={15} />
                  <span>Download Sample Products Data</span>
                </button>
              </div>

              {/* Instructions Card */}
              <div className="bg-white border border-slate-200 rounded-lg p-4 sm:p-5">
                <h4 className="text-xs sm:text-sm font-bold text-slate-900 mb-2">
                  Upload instructions
                </h4>
                <ul className="m-0 pl-4.5 flex flex-col gap-1.5 list-disc text-xs text-slate-600 leading-relaxed">
                  <li>
                    Product rows must include mandatory fields:{" "}
                    <strong className="text-slate-900">Title</strong>,{" "}
                    <strong className="text-slate-900">SKU ID</strong>,{" "}
                    <strong className="text-slate-900">Price</strong> (Max
                    limit: 99,99,999),{" "}
                    <strong className="text-slate-900">Mode</strong>,{" "}
                    <strong className="text-slate-900">Collection Category</strong>, and{" "}
                    <strong className="text-slate-900">Filter Categories</strong>.
                  </li>
                  <li>
                    Multiple filter categories can be comma-separated. If you
                    specify a filter category that is not currently present in
                    the system, it will automatically be created.
                  </li>
                  <li>
                    Valid <strong className="text-slate-900">Mode</strong>{" "}
                    values:{" "}
                    <strong className="text-slate-900">Mannequin to Model</strong>{" "}
                    or{" "}
                    <strong className="text-slate-900">Model to Model</strong>.
                  </li>
                  <li>
                    Ensure all mandatory fields are correctly filled for each
                    row before uploading. Empty rows are automatically ignored.
                  </li>
                  <li>
                    Maximum{" "}
                    <strong className="text-slate-900">250 product rows</strong>{" "}
                    allowed per upload (Maximum file size:{" "}
                    <strong className="text-slate-900">80MB</strong>).
                  </li>
                </ul>
              </div>

              {/* File Dropzone - Always Visible */}
              <div
                className={`bg-slate-50 border ${
                  isDragOver
                    ? "border-blue-500 bg-blue-50/50"
                    : "border-slate-300 hover:border-slate-400 hover:bg-slate-100/80"
                } rounded-lg py-5 px-4 min-h-[72px] text-center cursor-pointer transition-all flex items-center justify-center gap-2`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
              >
                <MdAttachFile size={16} className="text-slate-600 rotate-45" />
                <span className="text-xs font-semibold text-slate-700">
                  {selectedFile
                    ? `Change CSV file (${selectedFile.name})`
                    : "Choose CSV file"}
                </span>
              </div>

              {/* Validation Errors Table */}
              {validationErrors.length > 0 && (
                <div className="flex flex-col gap-2 animate-in fade-in duration-150">
                  <p className="m-0 text-xs font-semibold text-red-600">
                    Validation failed. Please correct these errors in your CSV
                    file and re-upload:
                  </p>
                  <div className="border border-red-200 rounded-lg overflow-hidden max-h-56 overflow-y-auto shadow-xs">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead className="bg-red-100 sticky top-0 z-10 border-b border-red-200">
                        <tr>
                          <th className="py-2 sm:py-2.5 px-2.5 sm:px-4 font-bold text-red-900 w-12 sm:w-16 bg-red-100">
                            ROW
                          </th>
                          <th className="py-2 sm:py-2.5 px-2.5 sm:px-4 font-bold text-red-900 w-20 sm:w-36 bg-red-100">
                            SKU
                          </th>
                          <th className="py-2 sm:py-2.5 px-2.5 sm:px-4 font-bold text-red-900 bg-red-100">
                            ERRORS
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-red-100 bg-white">
                        {validationErrors.map((item, index) => (
                          <tr
                            key={index}
                            className="hover:bg-red-50/50 transition-colors"
                          >
                            <td className="py-2.5 sm:py-3 px-2.5 sm:px-4 font-semibold text-slate-700 align-top">
                              {item.row}
                            </td>
                            <td className="py-2.5 sm:py-3 px-2.5 sm:px-4 font-semibold text-slate-900 align-top font-mono text-[11px] break-all">
                              {item.sku}
                            </td>
                            <td className="py-2.5 sm:py-3 px-2.5 sm:px-4 align-top">
                              <ul className="m-0 pl-3.5 sm:pl-4 list-disc space-y-1 text-red-600 font-medium break-words">
                                {item.errors.map((err, errIdx) => (
                                  <li key={errIdx}>{err}</li>
                                ))}
                              </ul>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Parsed Success Status Banner */}
              {selectedFile &&
                validationErrors.length === 0 &&
                parsedData.length > 0 && (
                  <div className="w-full bg-emerald-100/80 border border-emerald-300 text-emerald-800 text-xs font-semibold py-2.5 px-4 rounded-lg flex items-center justify-center gap-2 animate-in fade-in duration-150">
                    <MdCheck className="text-emerald-700 text-sm shrink-0" />
                    <span>
                      File parsed successfully:{" "}
                      <strong className="font-bold text-emerald-950">
                        {selectedFile.name}
                      </strong>{" "}
                      ({parsedData.length} valid product(s)). Ready to upload.
                    </span>
                  </div>
                )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2.5 p-4 px-6 sm:px-8 border-t border-slate-100 bg-white shrink-0">
          {uploadResult ? (
            <>
              <button
                type="button"
                className="bg-white border border-slate-300 hover:border-slate-400 text-slate-700 hover:text-slate-900 text-xs font-semibold px-4 py-1.5 sm:py-2 rounded-md transition-all inline-flex items-center gap-1.5 cursor-pointer shadow-xs"
                onClick={() => fileInputRef.current?.click()}
              >
                <MdUpload size={16} />
                <span>Upload Another CSV</span>
              </button>
              <button
                type="button"
                className="bg-blue-600 hover:bg-blue-700 text-white border border-blue-600 text-xs font-semibold px-5 py-1.5 sm:py-2 rounded-md transition-all inline-flex items-center gap-1.5 cursor-pointer shadow-xs"
                onClick={handleModalClose}
              >
                <MdClose size={15} />
                <span>Close</span>
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="bg-white border border-slate-300 hover:border-slate-400 text-slate-700 hover:text-slate-900 text-xs font-semibold px-4 py-1.5 sm:py-2 rounded-md transition-all inline-flex items-center gap-1.5 cursor-pointer"
                onClick={handleModalClose}
                disabled={uploading}
              >
                <MdClose size={15} />
                <span>Cancel</span>
              </button>
              <button
                type="button"
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-500/40 disabled:hover:bg-blue-500/40 text-white border border-blue-600 disabled:border-blue-500/30 text-xs font-semibold px-5 py-1.5 sm:py-2 rounded-md transition-all inline-flex items-center gap-1.5 cursor-pointer disabled:cursor-not-allowed shadow-xs"
                onClick={handleFormSubmit}
                disabled={
                  !selectedFile ||
                  validationErrors.length > 0 ||
                  parsedData.length === 0 ||
                  uploading
                }
              >
                {uploading ? (
                  <>
                    <MdSync className="animate-spin" size={16} />
                    <span>Uploading...</span>
                  </>
                ) : (
                  <>
                    <MdUpload size={16} />
                    <span>Upload</span>
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
